import React, { useState, useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import './Matchmaking.css';

const Matchmaking = ({ onMatchFound, onBack }) => {
  const { user } = useAuth();
  const { 
    connected, 
    inQueue, 
    queuePosition, 
    matchFound,
    joinQueue, 
    leaveQueue 
  } = useSocket();
  
  const [searching, setSearching] = useState(false);
  const [timeInQueue, setTimeInQueue] = useState(0);

  useEffect(() => {
    let interval;
    if (inQueue) {
      interval = setInterval(() => {
        setTimeInQueue(t => t + 1);
      }, 1000);
    } else {
      setTimeInQueue(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [inQueue]);

  useEffect(() => {
    if (matchFound) {
      onMatchFound(matchFound);
    }
  }, [matchFound, onMatchFound]);

  const handleJoinQueue = () => {
    if (!connected) {
      alert('Not connected to server. Please check your connection.');
      return;
    }
    
    setSearching(true);
    joinQueue(user.rating || 1200);
  };

  const handleLeaveQueue = () => {
    setSearching(false);
    leaveQueue();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="matchmaking-container">
      <div className="matchmaking-header">
        <button onClick={onBack} className="back-button">
          ← Back to Menu
        </button>
        <h1>Online Multiplayer</h1>
      </div>

      <div className="matchmaking-content">
        <div className="player-info-card">
          <h2>Your Profile</h2>
          <div className="player-stats">
            <div className="stat">
              <span className="stat-label">Username</span>
              <span className="stat-value">{user.username}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Rating</span>
              <span className="stat-value">{user.rating || 1200}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Games Played</span>
              <span className="stat-value">{user.gamesPlayed || 0}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Win Rate</span>
              <span className="stat-value">
                {user.gamesPlayed > 0 
                  ? `${Math.round((user.gamesWon / user.gamesPlayed) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>
        </div>

        <div className="matchmaking-status">
          {!connected && (
            <div className="status-message error">
              <h2>❌ Not Connected</h2>
              <p>Unable to connect to multiplayer server. Please refresh the page.</p>
            </div>
          )}

          {connected && !inQueue && !searching && (
            <div className="status-message ready">
              <h2>🎮 Ready to Play!</h2>
              <p>Find an opponent and start playing online</p>
              <button onClick={handleJoinQueue} className="join-queue-button">
                Find Match
              </button>
            </div>
          )}

          {inQueue && (
            <div className="status-message searching">
              <div className="searching-animation">
                <div className="spinner"></div>
              </div>
              <h2>🔍 Searching for Opponent...</h2>
              <div className="queue-info">
                <p>Queue Position: <strong>{queuePosition || '...'}</strong></p>
                <p>Time in Queue: <strong>{formatTime(timeInQueue)}</strong></p>
                <p className="queue-hint">Looking for players near rating {user.rating || 1200}</p>
              </div>
              <button onClick={handleLeaveQueue} className="cancel-queue-button">
                Cancel Search
              </button>
            </div>
          )}
        </div>

        <div className="matchmaking-info">
          <h3>How It Works</h3>
          <ul>
            <li>🎯 Click "Find Match" to join the matchmaking queue</li>
            <li>⚖️ We'll match you with players of similar rating</li>
            <li>🎲 Colors are randomly assigned</li>
            <li>⏱️ Standard time controls apply</li>
            <li>📊 Your rating updates after each game</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Matchmaking;
